import React, { useState, useEffect } from 'react';
import { Card, Button, Descriptions, Modal, Form, Input, Select, message, Upload, Avatar, Tabs, Space, Typography, Row, Col, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { updateUser } from '../api/users';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import LanguageSelector from '../components/common/LanguageSelector';
import CurrencySelector from '../components/common/CurrencySelector';
import type { User } from '../types';
import { UploadOutlined, UserOutlined, EditOutlined } from '@ant-design/icons';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import ContactEditModal from '../components/modal/ContactEditModal';
import type { Contact } from '../types/common';


const { Title } = Typography;


export const Settings: React.FC = () => {
  const { firebaseUser, firestoreUser, getProfilePicture } = useAuth();
  const { t } = useTranslation(['common', 'settings']);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isContactModalVisible, setIsContactModalVisible] = useState(false);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [cachedProfilePicture, setCachedProfilePicture] = useState<string>('');


  useEffect(() => {
    const loadProfilePicture = async () => {
      if (firestoreUser?.profilePicture?.url) {
        const cachedUrl = await getProfilePicture(firestoreUser.profilePicture.url);
        setCachedProfilePicture(cachedUrl);
      }
    };
    loadProfilePicture();
  }, [firestoreUser?.profilePicture?.url, getProfilePicture]);

  const updateUserMutation = useMutation({
    mutationFn: (data: Partial<User>) => updateUser(firebaseUser?.uid || '', data, firestoreUser!),
    onSuccess: () => {
      message.success(t('settings:updateSuccess'));
      setIsModalVisible(false);
      queryClient.invalidateQueries({ queryKey: ['user', firebaseUser?.uid] });
    },
    onError: () => {
      message.error(t('settings:updateError'));
    },
  });

  const handleUpdate = async (values: any) => {
    await updateUserMutation.mutateAsync(values);
  };

  const handleProfilePictureUpload = async (file: File) => {
    if (!firestoreUser?.workspace?.id || !firestoreUser?.workspace?.slug || !firestoreUser?.username) {
      message.error(t('settings:selectWorkspaceFirst'));
      return;
    }

    try {
      const storage = getStorage();
      const timestamp = Date.now();
      const path = `workspaces/${firestoreUser.workspace.slug}/users/${firestoreUser.username}/profile-pictures/${timestamp}_${file.name}`;
      const storageRef = ref(storage, path);

      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      const profilePicture: any = {
        id: storageRef.name,
        name: file.name,
        type: file.type,
        size: file.size,
        path,
        url,
        uploadedAt: new Date(),
        isPrimary: true
      };

      await updateUserMutation.mutateAsync({ profilePicture });
      message.success(t('settings:profilePictureUpdateSuccess'));
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      message.error(t('settings:profilePictureUpdateError'));
    }
  };

  const handleContactSave = async (contacts: Contact[]) => {
    await updateUserMutation.mutateAsync({ contacts });
    setIsContactModalVisible(false);
  };

  const getStatusTranslation = (status: string) => {
    return t(`common:status.${status}`);
  };

  const getRoleTranslation = (role: string) => {
    return t(`common:roles.${role}`);
  };

  const contactTypeOptions = [
    { value: 'primary', label: t('common:contactTypes.primary') },
    { value: 'secondary', label: t('common:contactTypes.secondary') },
    { value: 'emergency', label: t('common:contactTypes.emergency') },
  ];

  if (!firestoreUser) return null;

  const items = [
    {
      key: 'profile',
      label: t('settings:tabs.profile'),
      children: (
        <Card>
          <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col>
              <Space>
                <Avatar 
                  size={64} 
                  src={cachedProfilePicture || firestoreUser.profilePicture?.url}
                  icon={<UserOutlined />}
                />
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    handleProfilePictureUpload(file);
                    return false;
                  }}
                >
                  <Button icon={<UploadOutlined />}>{t('settings:changeProfilePicture')}</Button>
                </Upload>
              </Space>
            </Col>
          </Row>

          <Descriptions bordered column={1}>
            <Descriptions.Item label={t('settings:fields.email')}>{firestoreUser.email}</Descriptions.Item>
            <Descriptions.Item label={t('settings:fields.displayName')}>{firestoreUser.displayName}</Descriptions.Item>
            <Descriptions.Item label={t('settings:fields.status')}>
              <Tag color={firestoreUser.status === 'active' ? 'green' : 'red'}>
                {getStatusTranslation(firestoreUser.status)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('settings:fields.roles')}>
              <Space>
                {firestoreUser.roles.map(role => (
                  <Tag key={role} color="blue">
                    {getRoleTranslation(role)}
                  </Tag>
                ))}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label={t('settings:fields.workspace')}>{firestoreUser.workspace?.name}</Descriptions.Item>
            <Descriptions.Item label={t('settings:fields.language')}>
              <LanguageSelector value={firestoreUser.language} onChange={(value) => handleUpdate({ language: value })} />
            </Descriptions.Item>
            <Descriptions.Item label={t('settings:fields.currency')}>
              <CurrencySelector value={firestoreUser.currency} onChange={(value) => handleUpdate({ currency: value })} />
            </Descriptions.Item>
          </Descriptions>
        </Card>
      ),
    },
    {
      key: 'contacts',
      label: t('settings:tabs.contacts'),
      children: (
        <Card>
          <div style={{ marginBottom: 16 }}>
            <Button type="primary" onClick={() => setIsContactModalVisible(true)}>
              {t('common:addContact')}
            </Button>
          </div>
          {firestoreUser.contacts && firestoreUser.contacts.length > 0 ? (
            <div style={{ maxWidth: 600, margin: '0 auto' }}>
              <Descriptions bordered>
                {firestoreUser.contacts.map((contact, index) => (
                  <React.Fragment key={index}>
                    <Descriptions.Item label={t('common:name')}>{contact.name}</Descriptions.Item>
                    <Descriptions.Item label={t('common:email')}>{contact.email}</Descriptions.Item>
                    <Descriptions.Item label={t('common:phone')}>{contact.phone}</Descriptions.Item>
                  </React.Fragment>
                ))}
              </Descriptions>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '24px' }}>
              {t('settings:noContacts')}
            </div>
          )}
        </Card>
      ),
    },
  ];

  return (
    <div>
      <Card 
        title={
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={4} style={{ margin: 0 }}>{t('settings:title')}</Title>
            </Col>
            <Col>
              <Button 
                type="primary"
                icon={<EditOutlined />}
                onClick={() => setIsModalVisible(true)}
              >
                {t('settings:editButton')}
              </Button>
            </Col>
          </Row>
        }
      >
        <Tabs items={items} />
      </Card>

      <Modal
        title={t('settings:editTitle')}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={firestoreUser}
          onFinish={handleUpdate}
        >
          <Form.Item
            name="displayName"
            label={t('settings:fields.displayName')}
            rules={[{ required: true, message: t('common:fieldRequired') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="status"
            label={t('settings:fields.status')}
            rules={[{ required: true, message: t('common:fieldRequired') }]}
          >
            <Select>
              <Select.Option value="active">{t('common:status.active')}</Select.Option>
              <Select.Option value="inactive">{t('common:status.inactive')}</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="roles"
            label={t('settings:fields.roles')}
            rules={[{ required: true, message: t('common:fieldRequired') }]}
          >
            <Select mode="multiple">
              <Select.Option value="administrator">{t('common:roles.administrator')}</Select.Option>
              <Select.Option value="user">{t('common:roles.user')}</Select.Option>
              <Select.Option value="manager">{t('common:roles.manager')}</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={updateUserMutation.isPending}>
              {t('common:actions.save')}
            </Button>
            <Button onClick={() => setIsModalVisible(false)} style={{ marginLeft: 8 }}>
              {t('common:actions.cancel')}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <ContactEditModal
        open={isContactModalVisible}
        onCancel={() => setIsContactModalVisible(false)}
        onSave={handleContactSave}
        contactTypeOptions={contactTypeOptions}
        t={t}
      />
    </div>
  );
}; 